import { useCallback, useMemo, useState } from "react";
import { useT, Namespace, type TranslationKey } from "@racoon/i18n";
import {
  cn,
  DataTable,
  FilterBlock,
  GroupChevron,
  GroupHeaderRow,
  GROUPED_TABLE_CLASS_NAME,
  getGroupedRowClassName,
  useGroupedRows,
  useModal,
  type Column,
  type GroupedRowMarkers,
  type Row,
} from "@racoon/ui";
import { useGetOrganizationSettingsQuery } from "~/shared/api/organization/organization.grpc";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import {
  filterJobTypeOptions,
  useJobTypeOptions,
  type JobTypeOption,
} from "~/shared/hooks/useJobTypeOptions";
import { JOB_TYPE_CATEGORY_KEY } from "~/shared/lib/translationMaps";
import { JobTypeForm } from "./job-type-form";
import { JobTypeStatusPill } from "./job-type-status-pill";

// The design's group order; job types without a category are not configurable here.
const CATEGORY_ORDER = [
  "CATEGORY_MSW",
  "CATEGORY_CO",
  "CATEGORY_TIPPER",
  "CATEGORY_DISINFECTION",
  "CATEGORY_LIQUID",
];

type JobTypeRow = GroupedRowMarkers & {
  id: string;
  categoryKey: string;
  option?: JobTypeOption;
  index?: number;
  active?: boolean;
};

const NO_SORTING: never[] = [];

// ponytail: mirrors the default roles that manage organization_settings in go-backend
// (permify/provider_defaults.go); custom access groups are not read, the API still decides.
const SETTINGS_MANAGER_ROLES = new Set([
  "USER_ROLE_PROVIDER_OWNER",
  "USER_ROLE_PROVIDER_DISPATCHER",
  "USER_ROLE_PROVIDER_SALES",
]);

export const JobTypesSettings = () => {
  const t = useT();
  const { openModal, closeModal } = useModal();
  const { isAdmin, user, activeCompanyId } = useAuthSession();
  const canEdit = isAdmin || SETTINGS_MANAGER_ROLES.has(String(user?.role));
  const [searchValue, setSearchValue] = useState("");

  const { options } = useJobTypeOptions(t);
  const { data: settings, isLoading } = useGetOrganizationSettingsQuery({});
  const disabledTypes = useMemo(
    () => settings?.disabledJobTypes?.types ?? [],
    [settings?.disabledJobTypes?.types],
  );
  // GetSetting answers for the caller's own org only, so an admin inside another company
  // would see (and edit) their own list here.
  const isOtherCompany = !!settings && !!activeCompanyId && Number(settings.id) !== activeCompanyId;

  const rows = useMemo<JobTypeRow[]>(() => {
    const visible = filterJobTypeOptions(options, searchValue);
    const result: JobTypeRow[] = [];
    let index = 0;
    for (const categoryKey of CATEGORY_ORDER) {
      const inCategory = visible.filter(
        (option) => JOB_TYPE_CATEGORY_KEY[option.type] === categoryKey,
      );
      if (inCategory.length === 0) continue;
      result.push({ id: categoryKey, categoryKey, _isGroupHeader: true, _groupKey: categoryKey });
      inCategory.forEach((option, i) =>
        result.push({
          id: option.tariffKey,
          categoryKey,
          option,
          index: ++index,
          active: !disabledTypes.includes(option.type),
          _groupKey: categoryKey,
          _isLastInGroup: i === inCategory.length - 1,
        }),
      );
    }
    return result;
  }, [options, searchValue, disabledTypes]);

  const openEditModal = useCallback(
    (option: JobTypeOption) => {
      const modalId = openModal({
        title: t(Namespace.Settings, "jobTypes.dialog.title"),
        width: "30rem",
        bodyClassName: "!p-0",
        body: <JobTypeForm option={option} onClose={() => closeModal(modalId)} />,
      });
    },
    [t, openModal, closeModal],
  );

  const columns = useMemo<Column<JobTypeRow>[]>(
    () => [
      { id: "index", header: "#", accessor: (row) => row.index, width: 40, align: "center" },
      {
        id: "name",
        header: t(Namespace.Settings, "jobTypes.table.name"),
        accessor: (row) => row.option?.label,
      },
      {
        id: "endpoint",
        header: t(Namespace.Settings, "jobTypes.table.endpoint"),
        accessor: (row) => row.option?.tariffKey,
        width: 300,
      },
      {
        id: "status",
        header: t(Namespace.Settings, "jobTypes.table.status"),
        cell: ({ row }) => <JobTypeStatusPill active={row.original.active ?? true} />,
        width: 160,
        align: "center",
      },
      {
        id: "action",
        header: t(Namespace.Settings, "jobTypes.table.action"),
        cell: ({ row }) =>
          canEdit && row.original.option ? (
            <button
              type="button"
              id={`job-type-edit-${row.original.id}`}
              onClick={() => row.original.option && openEditModal(row.original.option)}
              className="text-sm text-text-information opacity-0 hover:underline focus-visible:opacity-100 group-hover/row:opacity-100"
            >
              {t(Namespace.Buttons, "edit")}
            </button>
          ) : null,
        width: 160,
        align: "center",
      },
    ],
    [t, canEdit, openEditModal],
  );

  const { data, isGroupExpanded, toggleGroup } = useGroupedRows<JobTypeRow>({
    rows,
    columns,
    sorting: NO_SORTING,
  });

  const renderCustomRow = useCallback(
    (row: Row<JobTypeRow>, _index: number, visibleColumnCount: number) => {
      const header = row.original;
      if (!header._isGroupHeader) return undefined;
      const expanded = isGroupExpanded(header);
      return (
        <GroupHeaderRow
          key={header.id}
          colSpan={visibleColumnCount}
          isExpanded={expanded}
          onToggle={() => toggleGroup(header)}
          className="!bg-slate-100"
          innerClassName="gap-4 px-4"
        >
          <GroupChevron isExpanded={expanded} size="sm" />
          <span className="text-base font-semibold leading-4 text-text-body">
            {t(Namespace.Enum, header.categoryKey as TranslationKey)}
          </span>
        </GroupHeaderRow>
      );
    },
    [isGroupExpanded, toggleGroup, t],
  );

  if (isOtherCompany) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-text-subtle">
        {t(Namespace.Settings, "jobTypes.otherOrganization")}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <FilterBlock
        direction="row"
        bgColor="white"
        searchValue={searchValue}
        onSearchChange={setSearchValue}
      />
      <DataTable<JobTypeRow>
        columns={columns}
        data={data}
        loading={isLoading}
        rowKey={(row) => row.id}
        renderCustomRow={renderCustomRow}
        manualSorting
        rowClassName={(row) =>
          cn(
            getGroupedRowClassName(row),
            "group/row !cursor-default",
            !row._isGroupHeader && "h-12 [&>td]:!py-2",
            !row._isGroupHeader && !row._isLastInGroup && "[&>td]:border-b",
          )
        }
        // The tab sits on white, not the page's gray the header corners are painted with
        className={cn(GROUPED_TABLE_CLASS_NAME, "[&_th]:!bg-white")}
        selectable={false}
        namespace={Namespace.Settings}
      />
    </div>
  );
};
